import React from 'react';
import { motion } from 'framer-motion';
import './LegalPage.css';

function PrivacyPolicy() {
  const isIOSSafari = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-safari');
  const inViewViewport = isIOSSafari ? { once: true, amount: 0.25 } : { once: true };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <main className="legal-page">
      <motion.div
        className="policy-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="policy-dates">
            Effective Date: August 27, 2025 | Last Updated: August 27, 2025
          </p>
        </div>
      </motion.div>

      <motion.div
        className="container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={inViewViewport}
      >
        <article className="legal-card">
          <motion.section variants={itemVariants} className="legal-section">
            <p className="intro-text">
              Versa Launch ("Company," "we," "our," or "us"), a subsidiary of Versa Launch LLC headquartered in Ponte Vedra Beach, Florida, respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website <a href="https://versalaunch.com">versalaunch.com</a>, our applications, and related services (the "Services").
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>1. Information We Collect</h2>
            <p>We may collect:</p>
            <ul>
              <li><strong>Personal Information:</strong> Name, email, phone number, and any details you provide when filling out forms or contacting us.</li>
              <li><strong>Business Information:</strong> Company name, project details, and service requests.</li>
              <li><strong>Usage Data:</strong> IP address, browser, device type, and pages visited.</li>
              <li><strong>Cookies & Tracking:</strong> Session cookies, analytics, and tracking technologies to improve your experience.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>2. How We Use Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Deliver, improve, and maintain our Services.</li>
              <li>Respond to inquiries and provide support.</li>
              <li>Communicate updates, promotions, and service notices.</li>
              <li>Customize user experience and analyze site performance.</li>
              <li>Comply with applicable legal requirements.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>3. Sharing of Information</h2>
            <p>We do not sell your personal data. We may share information with:</p>
            <ul>
              <li><strong>Service Providers</strong> (hosting, analytics, marketing).</li>
              <li><strong>Legal Compliance</strong> (to comply with laws or protect our rights).</li>
              <li><strong>Business Transactions</strong> (in a merger, sale, or acquisition).</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>4. Cookies & Tracking</h2>
            <p>We use cookies to improve performance. You may disable cookies, but some features may not work properly.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>5. Data Security</h2>
            <p>We use safeguards to protect your data but cannot guarantee 100% security.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>6. Your Rights</h2>
            <p>Depending on your region, you may:</p>
            <ul>
              <li>Request access, correction, or deletion of your data.</li>
              <li>Opt-out of marketing communications.</li>
              <li>Limit or object to processing.</li>
              <li>Contact us at <a href="mailto:support@versalaunch.com">support@versalaunch.com</a> or <a href="tel:19043104555">+1 (904) 310-4555</a> to exercise your rights.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>7. Children's Privacy</h2>
            <p>We do not knowingly collect information from children under 13.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>8. Third-Party Links</h2>
            <p>Our Services may link to external sites. We are not responsible for third-party practices.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>9. 10DLC Messaging Privacy & Compliance</h2>
            <p>If you choose to receive SMS/MMS communications from Versa Launch via Application-to-Person (A2P) 10DLC channels:</p>
            <ul>
              <li><strong>Consent:</strong> By providing your phone number and opting in, you consent to receive text messages related to updates, promotions, or service notices.</li>
              <li><strong>Message Frequency:</strong> Message frequency may vary based on service usage or campaign type.</li>
              <li><strong>Opt-Out:</strong> You may opt out at any time by replying STOP. You may also reply HELP for assistance. Standard carrier messaging and data rates may apply.</li>
              <li><strong>Data Usage:</strong> We collect your phone number, messaging history, and any responses you provide solely for the purpose of delivering SMS/MMS communications.</li>
              <li><strong>No Sale of Data:</strong> Your phone number and message data will never be sold to third parties.</li>
              <li><strong>Compliance:</strong> All SMS/MMS campaigns follow carrier rules, including restrictions on SHAFT content (sex, hate, alcohol, firearms, tobacco, controlled substances, and other prohibited categories).</li>
              <li><strong>Delivery:</strong> While we facilitate messaging, we cannot guarantee message delivery as it is subject to carrier networks and throughput limits.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>10. Changes</h2>
            <p>We may update this policy at any time. Check the "Last Updated" date for the latest revision.</p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>11. Contact</h2>
            <div className="contact-box">
              <p><strong>Versa Launch, a division of Versa Logix LLC</strong></p>
              <p>Jacksonville, Florida</p>
              <p>Email: <a href="mailto:support@versalaunch.com">support@versalaunch.com</a></p>
              <p>Phone: <a href="tel:19048634044">+1 (904) 863-4044</a></p>
            </div>
          </motion.section>
        </article>
      </motion.div>
    </main>
  );
}

export default PrivacyPolicy;
